import React, { useState } from 'react';

import { useParams } from 'react-router-dom';

import { Alert, Box, CircularProgress } from '@mui/material';

import RoomPolicyForm from '~/components/Common/Forms/RoomPolicyForm';

import { useGetRoomPolicySchema } from '~/hooks/apis/offers/useGetOffer';

import type { RoomPolicy } from '~/types/roomPolicy';

import CopyRoomAttributeButton from '../CopyRoomAttributeButton';
import type { RoomWithKey } from '../types';

interface Props {
  offerType?: string;
  onRoomPolicyChange?: (roomPolicy: RoomPolicy) => void;
  roomKey: string;
  roomPolicy?: RoomPolicy;
  rooms: Array<RoomWithKey>;
}

export default function PoliciesTab({ offerType, onRoomPolicyChange, roomKey, roomPolicy, rooms }: Props) {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const [copyVersion, setCopyVersion] = useState(0);
  const { data: schema, error: schemaError, isLoading: isLoadingSchema } = useGetRoomPolicySchema(offerId);

  if (isLoadingSchema) {
    return <CircularProgress size={24} />;
  }

  if (schemaError || !schema) {
    return <Alert severity="error">Unable to load room policy schema.</Alert>;
  }

  if (!roomPolicy) {
    return <Alert severity="info">This room is not attached to a package with room policies.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <CopyRoomAttributeButton
          attribute="room policies"
          roomKey={roomKey}
          rooms={rooms}
          onCopy={(sourceRoom) => {
            if (sourceRoom.room_policies === undefined) {
              return;
            }

            onRoomPolicyChange?.({ ...roomPolicy, description: sourceRoom.room_policies || '' });
            setCopyVersion((version) => version + 1);
          }}
        />
      </Box>
      <RoomPolicyForm
        key={copyVersion}
        hideSaveButton
        schema={schema}
        roomPolicy={roomPolicy}
        idOffer={offerId}
        offerType={offerType ?? ''}
        onRoomPolicyChange={onRoomPolicyChange}
      />
    </Box>
  );
}
